import { Component, inject, Input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from 'src/app/auth.service';
import { MediaDevicePermissionWatcherService } from 'src/app/media-device-permission-watcher.service';
import { AiRestrictedHoverDirective } from 'src/app/shared/directives/ai-restricted-hover.directive';
import { DynamicOverlayService } from 'src/app/shared/services/dynamic-overlay/dynamic-overlay.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { CreateChunkOptionsOverlayData } from 'src/app/views/content/chunk/create-chunk/create-chunk-options-overlay/create-chunk-options-overlay.component';
import { ContentStateService } from 'src/app/views/content/content-state.service';
import { PhotoRecorderDialogComponent } from './dialog/photo-recorder-dialog.component';

@Component({
  selector: 'app-photo-recorder',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, MatTooltipModule, TranslateModule, AiRestrictedHoverDirective],
  template: `
    @switch (displayMode) {
      @case ('interactive') {
        <div
          class="interactive-item"
          [class.disabled]="!canUseAI"
          (click)="openPhotoRecorder()"
          [matTooltip]="(canUseAI ? itemText : 'GenerateContent.NoAiAccessTooltip') | translate"
          [appAiRestrictedHover]="!canUseAI"
        >
          <div class="interactive-item__content">
            <div class="interactive-item__prefix"></div>
            <mat-icon class="interactive-item__icon" aria-hidden="true">{{ itemIcon }}</mat-icon>
            <span class="interactive-item__text">{{ itemText | translate }}</span>
          </div>
        </div>
      }
      @case ('icon') {
        <span [matTooltip]="(canUseAI ? itemText : 'GenerateContent.NoAiAccessTooltip') | translate" [appAiRestrictedHover]="!canUseAI">
          <button mat-icon-button (click)="openPhotoRecorder()" [disabled]="!canUseAI">
            <mat-icon>{{ itemIcon }}</mat-icon>
          </button>
        </span>
      }
    }
  `,
})
export class PhotoRecorderComponent {
  @Input() displayMode: 'interactive' | 'icon' = 'interactive';
  @Input() data!: CreateChunkOptionsOverlayData;

  itemText = 'MediaRecorder.TakePhoto';
  itemIcon = 'add_a_photo';

  private authService = inject(AuthService);
  canUseAI = this.authService.canUseAI();

  private dialog = inject(MatDialog);
  private contentStateService = inject(ContentStateService);
  private overlayService = inject(DynamicOverlayService);
  private permissionWatcher = inject(MediaDevicePermissionWatcherService);
  private toast = inject(ToastService);

  async openPhotoRecorder(): Promise<void> {
    if (!this.canUseAI) return;

    const cameraAccess = this.permissionWatcher.cameraPermissionState;

    if (cameraAccess === 'prompt') {
      const granted = await this.permissionWatcher.requestCameraAccess();
      if (!granted) {
        return;
      }
    } else if (cameraAccess === 'denied') {
      this.toast.showDevicePermissionError();
      return;
    }

    this.overlayService.close();
    const dialogRef = this.dialog.open(PhotoRecorderDialogComponent, {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      panelClass: 'full-screen-dialog',
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result: Blob | undefined) => {
      if (result instanceof Blob) {
        this.contentStateService.uploadRecording(result, 'image', this.data?.chunkAboveId);
      }
    });
  }
}
